import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { formatDateUS } from '../utils/dates';

const STATUS_VARIANT = {
  scheduled: 'info',
  active: 'primary',
  completed: 'success',
  canceled: 'secondary',
};

const TYPE_VARIANT = {
  primary: 'primary',
  general: 'secondary',
  runoff: 'warning',
  special: 'dark',
  municipal: 'info',
};

export default function ElectionsListPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [states, setStates] = useState([]);
  const [filters, setFilters] = useState({ state_id: '', type: '', year: '', status: '', q: '' });

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.get('/admin/states?page_size=100');
        setStates(res.data.items || []);
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ page: '1', page_size: '200' });
        Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
        const res = await adminApi.get(`/admin/elections?${params.toString()}`);
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } catch (e) {
        setError(extractError(e));
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [filters]);

  const years = useMemo(() => {
    const set = new Set();
    items.forEach((e) => { if (e.year) set.add(e.year); });
    return Array.from(set).sort((a, b) => b - a);
  }, [items]);

  return (
    <>
      <PageHeader
        title="Election Days"
        subtitle="Each row is one voting day for one jurisdiction. Attach Races and per-county Deadlines on the detail page."
        actions={<Button as={Link} to="/admin/elections/new" variant="primary" data-testid="elections-new-button"><i className="bi bi-plus-lg me-1" /> New election day</Button>}
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={4}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Search name</Form.Label>
                <Form.Control placeholder="e.g. Florida Primary" value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="elections-search-input" />
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>State</Form.Label>
                <Form.Select value={filters.state_id} onChange={(e) => setFilters((f) => ({ ...f, state_id: e.target.value }))} data-testid="elections-state-filter">
                  <option value="">All states</option>
                  {states.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Type</Form.Label>
                <Form.Select value={filters.type} onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))} data-testid="elections-type-filter">
                  <option value="">All types</option>
                  <option value="primary">Primary</option>
                  <option value="general">General</option>
                  <option value="runoff">Runoff</option>
                  <option value="special">Special</option>
                  <option value="municipal">Municipal</option>
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Status</Form.Label>
                <Form.Select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="elections-status-filter">
                  <option value="">All</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="canceled">Canceled</option>
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="text-muted" style={{ fontSize: '0.78rem' }}>Year</Form.Label>
                <Form.Select value={filters.year} onChange={(e) => setFilters((f) => ({ ...f, year: e.target.value }))} data-testid="elections-year-filter">
                  <option value="">All</option>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card>
          {loading ? (
            <div className="text-center p-5"><Spinner animation="border" /></div>
          ) : items.length === 0 ? (
            <div className="empty-state" data-testid="elections-empty">
              <i className="bi bi-calendar2-event" />
              <div>No election days match your filters.</div>
              <Button as={Link} to="/admin/elections/new" variant="primary" className="mt-2">Create the first election day</Button>
            </div>
          ) : (
            <Table hover responsive size="sm" className="mb-0" data-testid="elections-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Jurisdiction</th>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Status</th>
                  <th className="text-end">Races</th>
                  <th className="text-end">Deadlines</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} data-testid={`election-row-${e.id}`}>
                    <td><code>{formatDateUS(e.date)}</code></td>
                    <td>{e.jurisdiction?.name || '—'} <span className="text-muted">{e.state_abbr ? `· ${e.state_abbr}` : ''}</span></td>
                    <td><Badge bg={TYPE_VARIANT[e.type] || 'secondary'}>{e.type}</Badge></td>
                    <td>
                      <Link to={`/admin/elections/${e.id}`} className="text-decoration-none fw-semibold" data-testid={`election-link-${e.id}`}>
                        {e.name}
                      </Link>
                    </td>
                    <td><Badge bg={STATUS_VARIANT[e.status_computed] || 'secondary'}>{(e.status_computed || e.status || '').replace('_', ' ')}</Badge></td>
                    <td className="text-end text-muted">{e.races_count || 0}</td>
                    <td className="text-end text-muted">{e.deadlines_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
        <div className="text-muted mt-2" style={{ fontSize: '0.82rem' }}>{total} election day{total === 1 ? '' : 's'} match.</div>
      </div>
    </>
  );
}
